import type { JsonObject, JsonValue } from './json';
import type { ModelSchema, TaskModelSummary } from './models';
import { isNexraMediaInput, nexraMediaInputSchemaForMode } from './media-api';

export const SANDBOX_OPERATIONS = [
  { id: 'text-to-speech', modality: 'audio', label: '语音合成' },
  { id: 'text-to-music', modality: 'audio', label: '音乐生成' },
  { id: 'text-to-sfx', modality: 'audio', label: '音效生成' },
  { id: 'avatar-video', modality: 'video', label: '数字人视频' },
  { id: 'multimodal-to-video', modality: 'video', label: '全能参考生' },
  { id: 'text-to-video', modality: 'video', label: '文生视频' },
  { id: 'image-to-video', modality: 'video', label: '首尾帧生成视频' },
  { id: 'video-depth', modality: 'video', label: '视频深度提取' },
  { id: 'video-segment', modality: 'video', label: '视频目标分割' },
  { id: 'text-to-image', modality: 'image', label: '文生图' },
  { id: 'image-to-image', modality: 'image', label: '图生图' },
  { id: 'text-to-text', modality: 'language', label: '对话生成' },
] as const;
export type SandboxOperation = typeof SANDBOX_OPERATIONS[number]['id'];
export const sandboxObject = (v: unknown): JsonObject => v && typeof v === 'object' && !Array.isArray(v) ? v as JsonObject : {};
const obj = sandboxObject;
const array = (v: unknown): JsonValue[] => Array.isArray(v) ? v : [];

/** Catalog aliases are separate routes, not additional comparison models. */
export function sandboxIdentity(slug: string): string {
  return slug === 'google/veo-3-1-fast' ? 'google/veo-3-1-fast-generate-001' : slug;
}
export function sandboxModelModes(model: Pick<TaskModelSummary, 'slug' | 'modality' | 'capabilities'>): string[] {
  if (model.modality === 'language') return ['text-to-text'];
  if (model.modality === 'audio') {
    const features = new Set(model.capabilities.features ?? []);
    return [
      ...(features.has('tts') ? ['text-to-speech'] : []),
      ...(features.has('music') ? ['text-to-music'] : []),
      ...(features.has('sfx') ? ['text-to-sfx'] : []),
    ];
  }
  if (model.capabilities.features?.includes('avatar-video')) return ['avatar-video'];
  const referenceFeatures = model.capabilities.features ?? [];
  const hasFrameInput = referenceFeatures.some(feature => ['first-frame', 'first-last-frame'].includes(feature));
  const modes = model.capabilities.inputModes.filter(mode => ['text-to-image', 'image-to-image', 'text-to-video', 'image-to-video', 'video-depth', 'video-segment'].includes(mode))
    .filter(mode => !(mode === 'image-to-video' && (
      model.slug === 'pixverse/baidu-vod-pc1'
      || (referenceFeatures.includes('omni-reference') && !hasFrameInput)
    )));
  if (model.capabilities.inputModes.includes('audio-to-video')
    || model.capabilities.inputModes.includes('video-to-video')
    || referenceFeatures.some(feature => ['reference-image', 'reference-video', 'reference-audio', 'omni-reference'].includes(feature))) {
    modes.push('multimodal-to-video');
  }
  return modes;
}
export function sandboxOperations(models: TaskModelSummary[]) {
  return SANDBOX_OPERATIONS.filter(op => {
    const count = new Set(models
      .filter(m => m.modality === op.modality && sandboxModelModes(m).includes(op.id))
      .map(m => sandboxIdentity(m.slug))).size;
    return count >= 1;
  });
}
export function sandboxProperties(schema: ModelSchema) {
  const root = obj(schema.inputSchema.properties);
  return { root, parameters: obj(obj(root.parameters).properties) };
}
function mediaRoles(schema: ModelSchema): string[] {
  const { root } = sandboxProperties(schema);
  if (root.content) return array(obj(obj(root.content).items).oneOf).flatMap(v => array(obj(obj(obj(v).properties).role).enum)).filter((v): v is string => typeof v === 'string');
  return array(obj(obj(root.images)['x-ui']).roles).filter((v): v is string => typeof v === 'string');
}
export function sandboxSupports(schema: ModelSchema, feature: string): boolean {
  const roles = mediaRoles(schema);
  if (feature === 'last_frame') return roles.includes('last_frame');
  if (feature === 'reference_images') return roles.some(role => ['reference', 'reference_image', 'subject', 'background'].includes(role));
  if (feature === 'videos') return roles.includes('reference') || roles.includes('reference_video');
  if (feature === 'audios') return roles.includes('reference') || roles.includes('reference_audio');
  return false;
}
export function sandboxVoiceKey(model: string) { return `voice:${model}`; }

/** Experience Center already edits the public Nexra contract; no Provider-shape guessing remains here. */
export function adaptSandboxInput(_schema: ModelSchema, mode: string, input: JsonObject): JsonObject {
  if (!isNexraMediaInput(input)) throw new Error('输入必须使用有序 content parts');
  const parts = input.content as JsonObject[];
  const hasText = parts.some(part => part.type === 'text' && typeof part.text === 'string' && part.text.trim());
  const hasImage = parts.some(part => part.type === 'image');
  const hasAudio = parts.some(part => part.type === 'audio');
  const hasMedia = parts.some(part => part.type === 'image' || part.type === 'video' || part.type === 'audio');
  if (mode === 'avatar-video') {
    if (!hasImage) throw new Error('数字人视频需要输入人物图片');
    if (hasText === hasAudio) throw new Error('数字人视频需要在口播文案和已录制语音中选择一种');
    return structuredClone(input);
  }
  if (mode === 'video-depth') {
    if (parts.length !== 1 || parts[0]?.type !== 'video' || parts[0]?.role !== 'source') throw new Error('视频深度提取需要一个原视频');
    return structuredClone(input);
  }
  if (mode === 'video-segment' && (parts.length !== 2 || !parts.some(part => part.type === 'video' && part.role === 'source'))) throw new Error('视频目标分割需要一个原视频和目标描述');
  if (!hasText) throw new Error('请填写提示词或朗读文本');
  if ((mode === 'image-to-image' || mode === 'image-to-video') && !hasImage) throw new Error('当前模式需要输入图片');
  if (mode === 'multimodal-to-video' && !hasMedia) throw new Error('全能参考模式需要至少一个图片、视频或音频素材');
  if ((mode === 'text-to-image' || mode === 'text-to-video') && parts.some(part => part.type !== 'text')) throw new Error('当前模式只接受文本输入');
  return structuredClone(input);
}

/** Local draft controls only. A model schema is still required before execution. */
export function sandboxDraftSchema(mode: string): ModelSchema {
  if (mode === 'text-to-text') {
    const inputSchema: JsonObject = {
      type: 'object',
      properties: {
        prompt: { type: 'string', title: '提示词', minLength: 1, 'x-ui': { control: 'textarea', order: 0 } },
        system: { type: 'string', title: '系统提示词', 'x-ui': { control: 'textarea', order: 1 } },
        messages: {
          type: 'array', title: '对话历史（可选）',
          items: { type: 'object', properties: { role: { type: 'string', enum: ['user', 'assistant'] }, content: { type: 'string' } }, required: ['role', 'content'], additionalProperties: false },
          'x-ui': { control: 'json', order: 2 },
        },
      },
      required: ['prompt'],
      additionalProperties: false,
    };
    return { model: '', version: 1, dialect: 'https://json-schema.org/draft/2020-12/schema', inputSchema, outputSchema: {}, uiSchema: {}, defaults: {}, examples: [] };
  }
  const categorySchema = nexraMediaInputSchemaForMode(mode);
  if (!categorySchema) throw new Error(`Unsupported Nexra input mode: ${mode}`);
  const inputSchema: JsonObject = {
    ...categorySchema,
    properties: { content: obj(categorySchema.properties).content! },
  };
  const content: JsonObject[] = mode === 'video-depth' ? [{ type: 'video', role: 'source', source: '' }] : [{
    type: 'text',
    text: '',
    role: mode === 'text-to-speech' || mode === 'avatar-video' ? 'script' : 'prompt',
  }];
  if (mode === 'image-to-image' || mode === 'image-to-video' || mode === 'avatar-video') {
    content.push({
      type: 'image',
      source: '',
      role: mode === 'image-to-video' ? 'first_frame' : 'source',
    });
  }
  if (mode === 'video-segment') content.push({ type: 'video', role: 'source', source: '' });
  const defaults: JsonObject = { content };
  return {
    model: '',
    version: 1,
    dialect: 'https://json-schema.org/draft/2020-12/schema',
    inputSchema,
    outputSchema: {},
    uiSchema: {},
    defaults,
    examples: [defaults],
  };
}

/** The category defines the vocabulary; selected model contracts decide which
 * fields and constraints the shared Experience Center form can actually use. */
export function sandboxInputSchema(schemas: ModelSchema[], mode: string): ModelSchema {
  const draft = sandboxDraftSchema(mode);
  if (mode === 'text-to-text') return draft;
  if (schemas.length === 0) throw new Error('请至少选择一个模型');
  return {
    ...draft,
    model: schemas.map(s => s.model).join(','),
    version: Math.max(1, ...schemas.map(s => s.version)),
    inputSchema: sharedModelInputSchema(nexraMediaInputSchemaForMode(mode)!, schemas),
  };
}

function sharedModelInputSchema(categorySchema: JsonObject, schemas: ModelSchema[]): JsonObject {
  const categoryProperties = obj(categorySchema.properties);
  const modelProperties = schemas.map(schema => obj(schema.inputSchema.properties));
  const properties: JsonObject = {};
  for (const [key, categoryProperty] of Object.entries(categoryProperties)) {
    if (!modelProperties.every(model => key in model)) continue;
    const definitions = modelProperties.map(model => obj(model[key]));
    const property = key === 'content'
      ? sharedContentProperty(obj(categoryProperty), definitions)
      : sharedScalarProperty(obj(categoryProperty), definitions);
    if (property) properties[key] = property;
  }

  const keys = new Set(Object.keys(properties));
  const required = new Set<string>(array(categorySchema.required).filter((key): key is string => typeof key === 'string' && keys.has(key)));
  for (const schema of schemas) {
    for (const key of array(schema.inputSchema.required)) {
      if (typeof key === 'string' && keys.has(key)) required.add(key);
    }
  }
  const allOf = schemas.flatMap(schema => [
    ...array(schema.inputSchema.allOf).map(value => structuredClone(value)),
    ...(Array.isArray(schema.inputSchema.oneOf)
      ? [{ oneOf: structuredClone(schema.inputSchema.oneOf) }]
      : []),
  ]);
  return {
    ...structuredClone(categorySchema),
    properties,
    required: [...required],
    ...(allOf.length > 0 ? { allOf } : {}),
  };
}

function sharedScalarProperty(category: JsonObject, definitions: JsonObject[]): JsonObject | undefined {
  const first = definitions[0];
  if (!first) return undefined;
  const categoryUi = obj(category['x-ui']);
  const sourceUi = obj(first['x-ui']);
  const result: JsonObject = {
    ...structuredClone(category),
    ...structuredClone(first),
    'x-ui': { ...structuredClone(categoryUi), ...structuredClone(sourceUi), ...(categoryUi.order === undefined ? {} : { order: categoryUi.order }) },
  };
  const enums = definitions.map(definition => array(definition.enum)).filter(values => values.length > 0);
  if (enums.length > 0) {
    const common = enums[0]!.filter(value => enums.every(values => values.some(candidate => JSON.stringify(candidate) === JSON.stringify(value))));
    if (common.length === 0) return undefined;
    result.enum = common;
  }
  const constants = definitions.flatMap(definition => definition.const === undefined ? [] : [definition.const]);
  if (constants.length > 0) {
    if (!constants.every(value => JSON.stringify(value) === JSON.stringify(constants[0]))) return undefined;
    result.const = constants[0]!;
  }
  projectNumericBoundary(result, definitions, 'minimum', Math.max);
  projectNumericBoundary(result, definitions, 'exclusiveMinimum', Math.max);
  projectNumericBoundary(result, definitions, 'maximum', Math.min);
  projectNumericBoundary(result, definitions, 'exclusiveMaximum', Math.min);
  projectNumericBoundary(result, definitions, 'minLength', Math.max);
  projectNumericBoundary(result, definitions, 'maxLength', Math.min);
  projectNumericBoundary(result, definitions, 'minItems', Math.max);
  projectNumericBoundary(result, definitions, 'maxItems', Math.min);
  return result;
}

function sharedContentProperty(category: JsonObject, definitions: JsonObject[]): JsonObject | undefined {
  const result = sharedScalarProperty(category, definitions);
  if (!result) return undefined;
  // Model descriptions document the model's complete content vocabulary. The
  // Experience Center projects that vocabulary onto one selected mode, so
  // retaining the source description can advertise inputs that were removed
  // from the rendered schema (for example first-frame input in text-to-video).
  delete result.description;
  const categoryItems = obj(category.items);
  const categoryVariants = array(categoryItems.oneOf).map(obj);
  const sourceVariantSets = definitions.map(definition => array(obj(definition.items).oneOf).map(obj));
  const variants = categoryVariants.flatMap(categoryVariant => {
    const type = partType(categoryVariant);
    if (!type) return [];
    const matching = sourceVariantSets.map(source => source.find(variant => partType(variant) === type)).filter((value): value is JsonObject => Boolean(value));
    if (matching.length !== definitions.length) return [];
    const projected = sharedContentVariant(categoryVariant, matching);
    return projected ? [projected] : [];
  });
  if (variants.length === 0) return undefined;
  result.items = { ...categoryItems, oneOf: variants };
  const contentRules = definitions.flatMap(definition => array(definition.allOf).map(value => structuredClone(value)));
  result.allOf = [...array(category.allOf).map(value => structuredClone(value)), ...contentRules];
  return result;
}

function sharedContentVariant(category: JsonObject, definitions: JsonObject[]): JsonObject | undefined {
  const result = structuredClone(category);
  const properties = obj(result.properties);
  const role = obj(properties.role);
  const roleDefinitions = definitions.map(definition => obj(obj(definition.properties).role));
  const allowed = roleValues(role).filter(value => roleDefinitions.every(definition => {
    const values = roleValues(definition);
    return values.length === 0 || values.includes(value);
  }));
  if (roleValues(role).length > 0 && allowed.length === 0) return undefined;
  if (allowed.length === 1) {
    properties.role = { ...role, const: allowed[0]! };
    delete (properties.role as JsonObject).enum;
  } else if (allowed.length > 0) properties.role = { ...role, enum: allowed };
  const first = definitions[0];
  if (typeof first?.title === 'string') result.title = first.title;
  if (typeof first?.description === 'string') result.description = first.description;
  const propertyKeys = new Set(Object.keys(properties));
  const required = new Set(array(result.required).filter((key): key is string => typeof key === 'string' && propertyKeys.has(key)));
  for (const definition of definitions) {
    for (const key of array(definition.required)) {
      if (typeof key === 'string' && propertyKeys.has(key)) required.add(key);
    }
  }
  result.required = [...required];
  result.properties = properties;
  return result;
}

function partType(value: JsonObject): string | undefined {
  const type = obj(obj(value.properties).type).const;
  return typeof type === 'string' ? type : undefined;
}

function roleValues(value: JsonObject): string[] {
  if (typeof value.const === 'string') return [value.const];
  return array(value.enum).filter((role): role is string => typeof role === 'string');
}

function projectNumericBoundary(
  target: JsonObject,
  definitions: JsonObject[],
  key: 'minimum' | 'exclusiveMinimum' | 'maximum' | 'exclusiveMaximum' | 'minLength' | 'maxLength' | 'minItems' | 'maxItems',
  choose: (...values: number[]) => number,
): void {
  const values = definitions.flatMap(definition => typeof definition[key] === 'number' ? [definition[key] as number] : []);
  if (values.length > 0) target[key] = choose(...values);
}
